/**
 * Client Portal Service history (plan section 19): one customer's past approved work, judged by each
 * task's report rule. Read-only and portal-only; the tenant has no board, so the base manifest only
 * names the entity and the portal block carries everything, as Services does.
 */
import { ActivityEntityName, DomainType, EntityViewDisplay, ServiceHistoryRow } from '@jms/model';
import { clientPaths } from '@jms/tenant/module.jms.authorisation';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { FilterPeriodPreset } from '@workbuddy/ui/v2/blocks/filter';
import { EntityBoardPeriodLimit } from '@workbuddy/ui/v2/blocks/entity.board/period';
import { PlannedWorkCol } from '../shared/planned.work.board.columns';
import { clientCsvDownloadCommands } from '../shared/client.csv.download';

// The same CSV as Services, from the same board request, so the file is the rows on screen. Gated on
// the Services path: History is a view of that tab, not a tab of its own.
const serviceHistoryCsvCommands = () => clientCsvDownloadCommands({ entity: ActivityEntityName.ServiceHistory, path: clientPaths.Services, commandId: 'service-history.download-csv', title: 'Service History' });

export const serviceHistoryManifest = registerManifest<ServiceHistoryRow>({
  entity: ActivityEntityName.ServiceHistory,
  slug: 'service-history',
  domains: {
    [DomainType.ClientPortal]: {
      routes: {
        root: '/service-history',
        record: () => null,
      },
      board: {
        supportedDisplays: [EntityViewDisplay.Table],
        defaults: {
          /**
           * History is read a period at a time, past only: the read stops at yesterday, so only the
           * past and current presets mean anything. Last month is the question a client brings to it.
           */
          period: {
            field: 'date',
            presets: [FilterPeriodPreset.LastWeek, FilterPeriodPreset.LastFortnight, FilterPeriodPreset.LastMonth, FilterPeriodPreset.ThisWeek, FilterPeriodPreset.ThisFortnight, FilterPeriodPreset.ThisMonth],
            defaultPreset: () => FilterPeriodPreset.LastMonth,
            custom: true,
            // The server ends the read at yesterday; stopping the control at today keeps it from offering empty windows.
            notAfter: EntityBoardPeriodLimit.Today,
          },
          readonly: true,
          empty: { title: 'No service history in this period.' },
          table: {
            fillerColumn: true,
            columns: {
              byField: {
                date: PlannedWorkCol.date,
                name: PlannedWorkCol.title,
                site: PlannedWorkCol.site,
                serviceLine: PlannedWorkCol.line,
                dueDate: PlannedWorkCol.date,
                earliest: PlannedWorkCol.date,
                latest: PlannedWorkCol.date,
                approvedFor: PlannedWorkCol.date,
                completedOn: PlannedWorkCol.date,
                result: PlannedWorkCol.tag,
              },
            },
          },
          header: {
            title: 'Service History',
            showSearch: true,
            showPaginator: true,
            showViewOptions: false,
            hideAdd: true,
            primaryCommands: serviceHistoryCsvCommands,
            // Readonly drops header commands as mutation entry points; Download CSV writes nothing.
            keepPrimaryCommandsWhenReadonly: true,
          },
        },
      },
    },
  },
});
